/**
 * Admin VIP Services (Phase P6F)
 * ================================
 * Production-grade admin UI for the vip_service_catalog.
 * - Lists DB-backed services (active + archived).
 * - Create / edit via POST /api/admin/vip-services/upsert.
 * - Soft-disable via POST /api/admin/vip-services/disable.
 * - No mock data, no frontend-only state, no hardcoded lists.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X, Edit2, Archive, CheckCircle2, Loader2, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import api from '../../utils/api';

/**
 * Category vocabulary (controlled, not free chaos).
 * Values mirror the seed categories currently in the DB.
 * Admin picks from this list in the modal; no free-text creation.
 */
const CATEGORY_OPTIONS = [
  'transport',
  'accommodation',
  'support',
  'medical_support',
  'other',
];

const emptyForm = {
  slug: '',
  name: '',
  category: 'other',
  description: '',
  sort_order: 99,
  // P6H metadata
  ranking_weight: 1,
  is_filterable: true,
  is_searchable: true,
  is_premium_signal: false,
  is_offer_bindable: true,
  is_campaign_requireable: true,
  _existing: false,
};

function slugify(s) {
  return (s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64);
}

export default function AdminVipServices() {
  const navigate = useNavigate();

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [showArchived, setShowArchived] = useState(true);

  // Modal state (single modal used for create + edit)
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [slugEdited, setSlugEdited] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/vip-services?include_archived=true');
      setServices(res.data.services || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load VIP services');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* Derived, filtered list */
  const filtered = services.filter((s) => {
    if (!showArchived && s.status !== 'active') return false;
    if (!query.trim()) return true;
    const q = query.trim().toLowerCase();
    return (
      (s.slug || '').toLowerCase().includes(q) ||
      (s.name || '').toLowerCase().includes(q) ||
      (s.category || '').toLowerCase().includes(q)
    );
  });

  const activeCount = services.filter((s) => s.status === 'active').length;
  const archivedCount = services.length - activeCount;

  /* Modal handlers */
  const openCreate = () => {
    setForm({ ...emptyForm });
    setSlugEdited(false);
    setModalOpen(true);
  };
  const openEdit = (svc) => {
    setForm({
      slug: svc.slug || '',
      name: svc.name || '',
      category: CATEGORY_OPTIONS.includes(svc.category) ? svc.category : 'other',
      description: svc.description || '',
      sort_order: typeof svc.sort_order === 'number' ? svc.sort_order : 99,
      // P6H metadata — hydrate from DB
      ranking_weight: typeof svc.ranking_weight === 'number' ? svc.ranking_weight : 1,
      is_filterable: svc.is_filterable !== false,
      is_searchable: svc.is_searchable !== false,
      is_premium_signal: !!svc.is_premium_signal,
      is_offer_bindable: svc.is_offer_bindable !== false,
      is_campaign_requireable: svc.is_campaign_requireable !== false,
      _existing: true,
    });
    setSlugEdited(true); // don't auto-override existing slug
    setModalOpen(true);
  };
  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
    setForm(emptyForm);
    setSlugEdited(false);
  };

  const handleNameChange = (v) => {
    setForm((f) => {
      const next = { ...f, name: v };
      if (!slugEdited && !f._existing) next.slug = slugify(v);
      return next;
    });
  };
  const handleSlugChange = (v) => {
    setSlugEdited(true);
    setForm((f) => ({ ...f, slug: v.toLowerCase().replace(/[^a-z0-9\-]/g, '-').slice(0, 64) }));
  };

  const validate = () => {
    const name = form.name.trim();
    const slug = form.slug.trim();
    if (!name) return 'Name is required';
    if (!slug) return 'Slug is required';
    if (!/^[a-z0-9\-]{1,64}$/.test(slug)) return 'Slug must be lowercase, digits, or hyphens (1–64 chars)';
    // uniqueness check (client-side guard; backend is authoritative)
    if (!form._existing && services.some((s) => s.slug === slug)) {
      return `Slug "${slug}" already exists`;
    }
    if (!CATEGORY_OPTIONS.includes(form.category)) return 'Invalid category';
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSaving(true);
    try {
      await api.post('/admin/vip-services/upsert', {
        slug: form.slug.trim(),
        name: form.name.trim(),
        category: form.category,
        description: (form.description || '').trim(),
        sort_order: Number.isFinite(Number(form.sort_order)) ? Number(form.sort_order) : 99,
        // P6H metadata
        ranking_weight: Number.isFinite(Number(form.ranking_weight)) ? Number(form.ranking_weight) : 1,
        is_filterable: !!form.is_filterable,
        is_searchable: !!form.is_searchable,
        is_premium_signal: !!form.is_premium_signal,
        is_offer_bindable: !!form.is_offer_bindable,
        is_campaign_requireable: !!form.is_campaign_requireable,
      });
      toast.success(form._existing ? 'Service updated' : 'Service created');
      setModalOpen(false);
      setForm(emptyForm);
      setSlugEdited(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDisable = async (slug) => {
    try {
      await api.post('/admin/vip-services/disable', { slug });
      toast.success(`"${slug}" archived`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Disable failed');
    }
  };

  const handleReactivate = async (svc) => {
    try {
      // Upsert with same fields always sets status=active (backend behavior).
      await api.post('/admin/vip-services/upsert', {
        slug: svc.slug,
        name: svc.name,
        category: CATEGORY_OPTIONS.includes(svc.category) ? svc.category : 'other',
        description: svc.description || '',
        sort_order: svc.sort_order ?? 99,
        // P6H — reactivation resets archive defaults to sensible active values
        ranking_weight: typeof svc.ranking_weight === 'number' && svc.ranking_weight > 0 ? svc.ranking_weight : 1,
        is_filterable: true,
        is_searchable: true,
        is_premium_signal: !!svc.is_premium_signal,
        is_offer_bindable: true,
        is_campaign_requireable: true,
      });
      toast.success(`"${svc.slug}" re-activated`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'Re-activate failed');
    }
  };

  /* Render */
  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-vip-services-page">
      <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-3">

        {/* Top Bar */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 min-w-0">
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs"
              onClick={() => navigate('/treatx-admin/dashboard')}
              data-testid="vip-back">
              <ArrowLeft className="h-3.5 w-3.5 mr-0.5" /> Back
            </Button>
            <h1 className="text-base font-semibold text-[#122033] truncate" data-testid="vip-title">VIP Services</h1>
            <Badge className="text-[10px] bg-slate-100 text-slate-600 border border-slate-200" data-testid="vip-count-active">
              {activeCount} active
            </Badge>
            {archivedCount > 0 && (
              <Badge className="text-[10px] bg-amber-50 text-amber-700 border border-amber-200" data-testid="vip-count-archived">
                {archivedCount} archived
              </Badge>
            )}
          </div>
          <Button size="sm" className="h-7 text-[11px] gap-1 bg-violet-600 hover:bg-violet-700 text-white"
            onClick={openCreate} data-testid="vip-new-btn">
            <Plus className="h-3 w-3" /> New Service
          </Button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center gap-2 mb-2 bg-white border border-slate-200 rounded-md px-2 py-1.5">
          <div className="flex items-center gap-1 flex-1">
            <Search className="h-3.5 w-3.5 text-slate-400" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, slug or category…"
              className="flex-1 text-[11.5px] bg-transparent focus:outline-none"
              data-testid="vip-search-input"
            />
          </div>
          <label className="flex items-center gap-1 text-[10.5px] text-slate-600 cursor-pointer" title="Show archived rows">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="vip-show-archived"
            />
            Show archived
          </label>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="vip-table">
          <div className="grid grid-cols-12 px-2.5 py-1.5 bg-slate-50 border-b border-slate-100 text-[9.5px] uppercase tracking-wider text-slate-400">
            <div className="col-span-3">Name</div>
            <div className="col-span-2">Slug</div>
            <div className="col-span-2">Category</div>
            <div className="col-span-2">Description</div>
            <div className="col-span-1 text-center" title="Ranking weight">Weight</div>
            <div className="col-span-1 text-center" title="Sort order">Order</div>
            <div className="col-span-1 text-right">Actions</div>
          </div>

          {loading ? (
            <div className="p-6 text-center text-xs text-slate-400" data-testid="vip-loading">
              <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-400" data-testid="vip-empty">
              No services match your filter.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filtered.map((svc) => {
                const isActive = svc.status === 'active';
                return (
                  <div
                    key={svc.slug}
                    className={`grid grid-cols-12 items-center gap-2 px-2.5 py-1.5 hover:bg-slate-50/60 ${!isActive ? 'opacity-60' : ''}`}
                    data-testid={`vip-row-${svc.slug}`}
                  >
                    <div className="col-span-3 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-[11.5px] font-medium text-[#122033] truncate">{svc.name}</span>
                        {!isActive && (
                          <span className="text-[9px] px-1 py-0 rounded bg-amber-50 text-amber-700 border border-amber-200"
                            data-testid={`vip-status-${svc.slug}`}>{svc.status || 'inactive'}</span>
                        )}
                        {svc.is_premium_signal && <span className="text-[9px] px-1 py-0 rounded bg-violet-50 text-violet-600 border border-violet-200" data-testid={`vip-meta-premium-${svc.slug}`}>premium</span>}
                        {svc.is_filterable === false && <span className="text-[9px] px-1 py-0 rounded bg-slate-100 text-slate-500 border border-slate-200" data-testid={`vip-meta-nofilter-${svc.slug}`}>non-filterable</span>}
                        {svc.is_offer_bindable === false && <span className="text-[9px] px-1 py-0 rounded bg-slate-100 text-slate-500 border border-slate-200" data-testid={`vip-meta-nooffer-${svc.slug}`}>non-offer</span>}
                      </div>
                    </div>
                    <div className="col-span-2 text-[10.5px] font-mono text-slate-500 truncate" title={svc.slug}>{svc.slug}</div>
                    <div className="col-span-2">
                      <span className="text-[10px] px-1.5 py-0 rounded bg-slate-100 text-slate-600 border border-slate-200">
                        {svc.category || '—'}
                      </span>
                    </div>
                    <div className="col-span-2 text-[10.5px] text-slate-500 truncate" title={svc.description}>
                      {svc.description || <span className="text-slate-300">—</span>}
                    </div>
                    <div className="col-span-1 text-center text-[10.5px] font-mono text-slate-600" data-testid={`vip-meta-weight-${svc.slug}`} title="ranking weight">
                      {typeof svc.ranking_weight === 'number' ? svc.ranking_weight : '—'}
                    </div>
                    <div className="col-span-1 text-center text-[10.5px] font-mono text-slate-500">
                      {svc.sort_order ?? '—'}
                    </div>
                    <div className="col-span-1 flex items-center justify-end gap-0.5">
                      <button
                        title="Edit"
                        className="h-6 w-6 flex items-center justify-center rounded text-slate-400 hover:bg-violet-50 hover:text-violet-600"
                        onClick={() => openEdit(svc)}
                        data-testid={`vip-edit-${svc.slug}`}
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                      {isActive ? (
                        <button
                          title="Archive (soft-delete)"
                          className="h-6 w-6 flex items-center justify-center rounded text-slate-400 hover:bg-red-50 hover:text-red-500"
                          onClick={() => handleDisable(svc.slug)}
                          data-testid={`vip-archive-${svc.slug}`}
                        >
                          <Archive className="h-3.5 w-3.5" />
                        </button>
                      ) : (
                        <button
                          title="Re-activate"
                          className="h-6 w-6 flex items-center justify-center rounded text-slate-400 hover:bg-emerald-50 hover:text-emerald-600"
                          onClick={() => handleReactivate(svc)}
                          data-testid={`vip-reactivate-${svc.slug}`}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <p className="mt-3 text-[9.5px] text-slate-400 text-center">
          VIP Services are soft-deleted (never physically removed). Archived entries are hidden from provider selection.
        </p>
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="vip-modal">
          <div className="bg-white rounded-md border border-slate-200 shadow-lg w-full max-w-md mx-3">
            <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
              <h2 className="text-sm font-semibold text-[#122033]">
                {form._existing ? `Edit · ${form.slug}` : 'New VIP Service'}
              </h2>
              <button onClick={closeModal} className="h-6 w-6 flex items-center justify-center rounded text-slate-400 hover:bg-slate-100"
                data-testid="vip-modal-close">
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="p-3 space-y-2.5">
              <Field label="Name *">
                <input
                  type="text" value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className="w-full text-[12px] border border-slate-200 rounded px-2 py-1.5 focus:ring-1 focus:ring-violet-300 focus:outline-none"
                  placeholder="e.g. Limo Transfer"
                  data-testid="vip-field-name"
                />
              </Field>
              <Field label="Slug * (lowercase, digits, hyphens)">
                <input
                  type="text" value={form.slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  disabled={form._existing}
                  className={`w-full text-[12px] font-mono border border-slate-200 rounded px-2 py-1.5 focus:ring-1 focus:ring-violet-300 focus:outline-none ${form._existing ? 'bg-slate-50 text-slate-500' : ''}`}
                  placeholder="e.g. limo-transfer"
                  data-testid="vip-field-slug"
                />
                {form._existing && (
                  <p className="text-[9.5px] text-slate-400 mt-0.5">Slug is immutable once created.</p>
                )}
              </Field>
              <Field label="Category (controlled vocabulary)">
                <select
                  value={form.category}
                  onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                  className="w-full text-[12px] border border-slate-200 rounded px-2 py-1.5 bg-white focus:ring-1 focus:ring-violet-300 focus:outline-none"
                  data-testid="vip-field-category"
                >
                  {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </Field>
              <Field label="Description">
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className="w-full text-[12px] border border-slate-200 rounded px-2 py-1.5 focus:ring-1 focus:ring-violet-300 focus:outline-none"
                  placeholder="Short description shown to providers"
                  data-testid="vip-field-description"
                />
              </Field>
              <Field label="Sort Order (smaller = earlier)">
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))}
                  className="w-32 text-[12px] font-mono border border-slate-200 rounded px-2 py-1.5 focus:ring-1 focus:ring-violet-300 focus:outline-none"
                  data-testid="vip-field-sort"
                />
              </Field>

              {/* P6H — Step-5C metadata */}
              <div className="pt-1 mt-1 border-t border-slate-100">
                <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-1.5">Search & Ranking Metadata</p>
                <Field label="Ranking Weight (0–10)">
                  <input
                    type="number" min="0" max="10"
                    value={form.ranking_weight}
                    onChange={(e) => setForm((f) => ({ ...f, ranking_weight: e.target.value }))}
                    className="w-32 text-[12px] font-mono border border-slate-200 rounded px-2 py-1.5 focus:ring-1 focus:ring-violet-300 focus:outline-none"
                    data-testid="vip-field-ranking-weight"
                  />
                </Field>
                <label className="flex items-center gap-2 text-[12px] text-slate-700 mt-1.5">
                  <input type="checkbox" checked={!!form.is_filterable}
                    onChange={(e) => setForm((f) => ({ ...f, is_filterable: e.target.checked }))}
                    data-testid="vip-field-filterable" />
                  Filterable (available as search filter chip)
                </label>
                <label className="flex items-center gap-2 text-[12px] text-slate-700 mt-1">
                  <input type="checkbox" checked={!!form.is_searchable}
                    onChange={(e) => setForm((f) => ({ ...f, is_searchable: e.target.checked }))}
                    data-testid="vip-field-searchable" />
                  Searchable (indexable by keyword)
                </label>
                <label className="flex items-center gap-2 text-[12px] text-slate-700 mt-1">
                  <input type="checkbox" checked={!!form.is_premium_signal}
                    onChange={(e) => setForm((f) => ({ ...f, is_premium_signal: e.target.checked }))}
                    data-testid="vip-field-premium" />
                  Premium signal (ranking heuristic)
                </label>
                <label className="flex items-center gap-2 text-[12px] text-slate-700 mt-1">
                  <input type="checkbox" checked={!!form.is_offer_bindable}
                    onChange={(e) => setForm((f) => ({ ...f, is_offer_bindable: e.target.checked }))}
                    data-testid="vip-field-offer-bindable" />
                  Offer-bindable (can be attached to offers)
                </label>
                <label className="flex items-center gap-2 text-[12px] text-slate-700 mt-1">
                  <input type="checkbox" checked={!!form.is_campaign_requireable}
                    onChange={(e) => setForm((f) => ({ ...f, is_campaign_requireable: e.target.checked }))}
                    data-testid="vip-field-campaign-requireable" />
                  Campaign-requireable (can be mandated by campaigns)
                </label>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-3 py-2 border-t border-slate-100 bg-slate-50/40">
              <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={closeModal} disabled={saving}
                data-testid="vip-modal-cancel">
                Cancel
              </Button>
              <Button size="sm" className="h-7 text-[11px] gap-1 bg-violet-600 hover:bg-violet-700 text-white"
                onClick={handleSave} disabled={saving}
                data-testid="vip-modal-save">
                {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                {form._existing ? 'Save changes' : 'Create service'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</span>
      {children}
    </label>
  );
}
